<template>
  <header class="app-header sticky top-0 z-[80] flex items-center px-6 h-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-all">
    
    <!-- Mobile Toggle -->
    <button 
      @click="store.toggleSidebar()" 
      id="button-toggle-menu" 
      class="p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 md:hidden transition-colors"
    >
      <span class="sr-only">Menu</span>
      <i class="uil uil-bars text-2xl"></i>
    </button>

    <!-- Page Title -->
    <div class="flex flex-col">
       <span class="text-[10px] font-black text-pink-600 uppercase tracking-widest leading-none mb-1">Visão Geral</span>
       <h4 class="text-slate-900 dark:text-white text-lg font-black tracking-tight leading-none" id="title-page-panel">Painel</h4>
    </div>

    <!-- Actions Section -->
    <div class="ms-auto flex items-center gap-4">
      <!-- Search Placeholder (can be expanded later) -->
      <div class="hidden lg:flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl px-4 py-2 border border-transparent focus-within:border-pink-300 transition-all w-64 group">
        <i class="uil uil-search text-slate-400 group-focus-within:text-pink-500"></i>
        <input type="text" placeholder="Pesquisar..." class="bg-transparent border-none focus:ring-0 text-sm font-bold text-slate-700 dark:text-slate-200 placeholder-slate-400 w-full ml-2">
      </div>

      <!-- Notifications -->
      <div ref="notificationsRef" class="relative">
        <button
          type="button"
          @click="toggleNotifications"
          class="p-2.5 text-slate-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-slate-800 rounded-xl transition-all relative"
          :class="{ 'text-pink-600 bg-pink-50 dark:bg-slate-800': notificationsOpen }"
          aria-label="Abrir notificações"
        >
          <i class="uil uil-bell text-xl"></i>
          <span
            v-if="unreadCount > 0"
            class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-600 text-white text-[10px] font-black flex items-center justify-center border-2 border-white dark:border-slate-900"
          >
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>

        <transition name="notifications-pop">
          <div
            v-if="notificationsOpen"
            class="absolute right-0 mt-3 w-[min(92vw,380px)] rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-2xl shadow-slate-900/15 overflow-hidden z-[120]"
          >
            <div class="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
              <div>
                <h3 class="text-sm font-black text-slate-900 dark:text-white">Notificações</h3>
                <p class="text-[11px] font-bold text-slate-400">{{ unreadCount }} não lida{{ unreadCount === 1 ? '' : 's' }}</p>
              </div>
              <button
                type="button"
                @click="loadNotifications"
                :disabled="notificationsLoading"
                class="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-900 text-slate-500 hover:text-pink-600 disabled:opacity-50"
                aria-label="Atualizar notificações"
              >
                <i class="uil uil-sync" :class="{ 'inline-block animate-spin': notificationsLoading }"></i>
              </button>
            </div>

            <div class="max-h-[420px] overflow-y-auto">
              <div v-if="notificationsLoading && notifications.length === 0" class="p-8 text-center">
                <div class="w-8 h-8 mx-auto rounded-full border-4 border-pink-500 border-b-transparent animate-spin"></div>
              </div>

              <button
                v-for="item in notifications"
                :key="item.id"
                type="button"
                @click="markAsRead(item)"
                class="w-full text-left px-5 py-4 border-b border-slate-100 dark:border-slate-900 hover:bg-slate-50 dark:hover:bg-slate-900/70 transition-colors flex gap-3"
              >
                <span
                  class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  :class="severityClass(item.severity)"
                >
                  <i :class="severityIcon(item.severity)"></i>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="flex items-start justify-between gap-2">
                    <span class="text-sm font-black text-slate-900 dark:text-white leading-tight">{{ item.title }}</span>
                    <span v-if="item.status === 'unread'" class="w-2 h-2 mt-1.5 rounded-full bg-pink-600 shrink-0"></span>
                  </span>
                  <span class="block text-xs font-semibold text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{{ item.message }}</span>
                  <span v-if="item.metadata?.error" class="block text-[11px] font-bold text-red-500 mt-1 line-clamp-1">
                    {{ item.metadata.error }}
                  </span>
                  <span class="block text-[10px] font-black uppercase tracking-widest text-slate-400 mt-2">{{ formatNotificationDate(item.created_at) }}</span>
                </span>
              </button>

              <div v-if="!notificationsLoading && notifications.length === 0" class="p-8 text-center">
                <div class="w-12 h-12 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-900 text-slate-400 flex items-center justify-center mb-3">
                  <i class="uil uil-bell-slash text-xl"></i>
                </div>
                <p class="text-sm font-black text-slate-700 dark:text-slate-200">Nenhuma notificação</p>
                <p class="text-xs font-semibold text-slate-400 mt-1">Quando algo importante acontecer, aparece aqui.</p>
              </div>
            </div>

            <NuxtLink
              v-if="route.params.id"
              :to="`/painel/org/${route.params.id}/notificacoes`"
              @click="notificationsOpen = false"
              class="block px-5 py-3 text-center text-xs font-black uppercase tracking-widest text-pink-600 hover:bg-pink-50 dark:hover:bg-slate-900 border-t border-slate-100 dark:border-slate-800"
            >
              Ver histórico completo
            </NuxtLink>
          </div>
        </transition>
      </div>

      <!-- Logout -->
      <NuxtLink 
        to="/auth/sair" 
        id="button-toggle-profile" 
        class="flex items-center gap-2 pl-4 border-l border-slate-200 dark:border-slate-700 hover:text-pink-600 transition-all group"
      >
        <div class="hidden sm:flex flex-col items-end mr-1">
          <span class="text-xs font-black text-slate-900 dark:text-white">Minha Conta</span>
          <span class="text-[10px] font-bold text-slate-400">Sair</span>
        </div>
        <div class="p-2.5 bg-slate-100 dark:bg-slate-800 rounded-xl group-hover:bg-pink-100 group-hover:text-pink-600 transition-all">
          <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 24 24">
            <path fill="currentColor" d="M4 12a1 1 0 0 0 1 1h7.59l-2.3 2.29a1 1 0 0 0 0 1.42a1 1 0 0 0 1.42 0l4-4a1 1 0 0 0 .21-.33a1 1 0 0 0 0-.76a1 1 0 0 0-.21-.33l-4-4a1 1 0 1 0-1.42 1.42l2.3 2.29H5a1 1 0 0 0-1 1M17 2H7a3 3 0 0 0-3 3v3a1 1 0 0 0 2 0V5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-3a1 1 0 0 0-2 0v3a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V5a3 3 0 0 0-3-3"/>
          </svg>
        </div>
      </NuxtLink>
    </div>
  </header>
</template>

<script lang="js" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAppStore } from '@/stores/index';

const store = useAppStore();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const token = useCookie('token');
const apidomain = runtimeConfig.public.NUXT_API_BACK_URL;

const notificationsRef = ref(null);
const notificationsOpen = ref(false);
const notificationsLoading = ref(false);
const notifications = ref([]);
const unreadCount = ref(0);

watch(route, () => {
  // Close sidebar on route change if mobile
  if (store.sidebar && window.innerWidth < 768) {
    store.toggleSidebar();
  }
  notificationsOpen.value = false;
});

const authHeaders = () => ({ authorization: `Bearer ${token.value}` });

const loadNotifications = async () => {
  if (!token.value) return;
  notificationsLoading.value = true;
  try {
    const [itemsResponse, countResponse] = await Promise.all([
      $fetch(`${apidomain}/notifications/me`, {
        headers: authHeaders(),
        params: { limit: 8 },
      }),
      $fetch(`${apidomain}/notifications/unread-count`, {
        headers: authHeaders(),
      }),
    ]);

    notifications.value = itemsResponse?.items || [];
    unreadCount.value = countResponse?.total || 0;
  } catch (error) {
    console.error('Erro ao carregar notificações:', error);
  } finally {
    notificationsLoading.value = false;
  }
};

const toggleNotifications = async () => {
  notificationsOpen.value = !notificationsOpen.value;
  if (notificationsOpen.value) {
    await loadNotifications();
  }
};

const markAsRead = async (item) => {
  if (!item || item.status === 'read') return;
  try {
    await $fetch(`${apidomain}/notifications/${item.id}/read`, {
      method: 'PUT',
      headers: authHeaders(),
    });
    item.status = 'read';
    unreadCount.value = Math.max(0, unreadCount.value - 1);
  } catch (error) {
    console.error('Erro ao marcar notificação como lida:', error);
  }
};

const handleOutsideClick = (event) => {
  if (!notificationsOpen.value || !notificationsRef.value) return;
  if (!notificationsRef.value.contains(event.target)) {
    notificationsOpen.value = false;
  }
};

const severityClass = (severity) => {
  if (severity === 'error') return 'bg-red-100 text-red-600 dark:bg-red-950/50';
  if (severity === 'success') return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50';
  if (severity === 'warning') return 'bg-amber-100 text-amber-600 dark:bg-amber-950/50';
  return 'bg-blue-100 text-blue-600 dark:bg-blue-950/50';
};

const severityIcon = (severity) => {
  if (severity === 'error') return 'uil uil-exclamation-triangle';
  if (severity === 'success') return 'uil uil-check-circle';
  if (severity === 'warning') return 'uil uil-exclamation-circle';
  return 'uil uil-info-circle';
};

const formatNotificationDate = (value) => {
  if (!value) return '';
  return new Date(Number(value)).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
  loadNotifications();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>

<style scoped>
.app-header {
  width: 100%;
}

.notifications-pop-enter-active,
.notifications-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.notifications-pop-enter-from,
.notifications-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
</style>
